"use client";

import { useEffect } from "react";
import { EmptyState } from "@focal/ui-kit";
import { sendLog } from "@/api";
import { LogLevel } from "@/types";

/** Props Next.js passes to an `error.tsx` boundary component. */
export interface ErrorBoundaryProps {
  error: Error & { digest?: string };
  reset: () => void;
}

interface RouteErrorProps extends ErrorBoundaryProps {
  /** Route segment this boundary guards, e.g. "conversations". Reported with
   *  the log so a crash narrows to a section even though production stacks are
   *  minified and no sourcemap is published. */
  segment: string;
}

/**
 * Shared fallback for the per-segment `error.tsx` boundaries.
 *
 * Without these, any render- or commit-phase error escalates to the root
 * `app/error.tsx` and whitescreens the whole app — including the sidebar and
 * header, which are rendered by the root layout and are almost never the thing
 * that broke. A segment boundary keeps the shell interactive so the user can
 * navigate away instead of reloading.
 *
 * `reset()` re-renders the segment in place; it deliberately does not reload
 * the page, since surviving the error without losing app state is the point.
 */
export const RouteError = ({ segment, error, reset }: RouteErrorProps) => {
  useEffect(() => {
    sendLog({
      level: LogLevel.ERROR,
      message: error.message,
      error: error.stack,
      component: `RouteError:${segment}`,
      data: {
        name: error.name,
        route: window.location.pathname + window.location.search,
        digest: error.digest ?? "",
      },
    }).catch((logError) => {
      console.error("Failed to log error", logError);
    });
  }, [error, segment]);

  return (
    <div
      className="flex flex-1 items-center justify-center p-6"
      data-cy={`route-error-${segment}`}
    >
      <EmptyState
        title="Something went wrong"
        description="This page couldn't be loaded. Our team has been notified. You can retry, or use the sidebar to go somewhere else."
        actionLabel="Try again"
        onAction={reset}
      />
    </div>
  );
};
